import {useRef} from 'react';
import cx from 'classnames';
import {useCanvasImage} from '~/common/canvas/hooks/useCanvasImage';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {Button} from '~library/atoms/Button';
import {useCardDimensions} from './hooks/useCardDimensions';
import {useCardVideo} from './hooks/useCardVideo';

import './Card.sass';

const CardImage = ({image, style, imageRef, redirectTo, loading, isMp4}) => (
  <img
    id="fabric-card"
    src={image}
    className={cx(
      'fabric_user_card',
      {
        'fabric_user_card--clickable': redirectTo,
        'envelope_image__wrapper--clickable--disabled': redirectTo && loading,
        'fabric_user_card--mp4': isMp4,
      },
      'greeting-card-flow'
    )}
    style={style}
    ref={imageRef}
    alt=""
  />
);

const BackgroundVideo = ({src, isFree, isLargishAndUp}) => (
  <video
    className={cx('fabric_user_card--mp4_background', {
      fabric_user_card: !isFree && isLargishAndUp,
    })}
    autoPlay
    loop
    muted
    playsInline
  >
    <source src={src} type="video/mp4" />
  </video>
);

const UYOVideo = ({uyoVideo, frameImage, videoStyles, videoRef}) => (
  <div className="fabric_user_card--mp4_background" ref={videoRef}>
    {frameImage && (
      <img className="fabric_user_card--mp4_background__frame" src={frameImage.src} alt="" />
    )}
    <video className="fabric-user-card__video" autoPlay loop muted playsInline style={videoStyles}>
      <source src={uyoVideo.src} type="video/mp4" />
    </video>
  </div>
);

export const Card = ({
  className = '',
  fabricArt,
  imageRef,
  redirectTo,
  isFree = false,
  parentNode,
}) => {
  const cardWrapperRef = useRef(null);
  const videoRef = useRef(null);
  const {image, loading} = useCanvasImage(fabricArt);
  const isLargishAndUp = useIsLargishAndUp();
  const isMobile = !isLargishAndUp;

  const dimensions = useCardDimensions(parentNode, cardWrapperRef, isMobile, isFree);
  const {videoStyles, isMp4, isGif, uyoVideo, frameImage, backgroundImage} = useCardVideo(
    fabricArt,
    videoRef
  );

  const navigate = useNavigator({
    isMobile,
    disableAutoOpenEditor: redirectTo === Sections.CARD_DESIGN,
  });

  const style = {
    ...dimensions,
    ...(isGif &&
      backgroundImage?.src && {
        backgroundImage: `url(${backgroundImage?.src})`,
        backgroundSize: '100%',
        backgroundRepeat: 'no-repeat',
      }),
  };

  return (
    <>
      {loading && <span className="fabric_user_card-spinner evt-spinner inline" />}
      <Button
        data-qa-id="card-img"
        variant="transparent"
        onClick={!loading && redirectTo ? () => navigate(redirectTo) : undefined}
        className={cx(className, {
          'container--mp4': isMp4,
        })}
        ref={cardWrapperRef}
      >
        <CardImage
          image={image}
          style={style}
          imageRef={imageRef}
          redirectTo={redirectTo}
          loading={loading}
          isMp4={isMp4}
        />
        {isMp4 && backgroundImage?.src && (
          <BackgroundVideo
            src={backgroundImage.src}
            isFree={isFree}
            isLargishAndUp={isLargishAndUp}
          />
        )}
        {uyoVideo && (
          <UYOVideo
            uyoVideo={uyoVideo}
            frameImage={frameImage}
            videoStyles={videoStyles}
            videoRef={videoRef}
          />
        )}
      </Button>
    </>
  );
};
